import { insumosDaPeca } from "../caso.js";
import { useEstado, usePode } from "../estado.jsx";
import { useNavegacao } from "../navegacao.jsx";
import { ICONES } from "../visual.jsx";
import { Etiqueta, Widget } from "./comuns.jsx";

/**
 * A petição, como widget da coluna do caso.
 *
 * Era um botão no cabeçalho da conversa — "Petição", o único sólido do topo — e
 * ele estava no lugar errado por dois motivos. O primeiro é de espaço: o topo da
 * conversa é a faixa mais disputada da mesa (nome do cliente, telefone, tese,
 * presença, mais três ações), e a petição empurrava o nome para as reticências
 * num notebook com o painel aberto. O segundo é de assunto: petição é ESTADO DO
 * CASO, como documento e prazo, e o lugar do estado do caso é a coluna dele.
 *
 * Como widget ele diz o que o botão não dizia: se a peça existe, em que ponto
 * está, e o que falta para ela sair boa. O botão era um ícone que abria uma tela
 * — a pessoa tinha de abrir para descobrir se havia algo lá dentro.
 *
 * Os dois estados:
 *
 *   **sem peça** — o widget é o convite, com a conta do que já está pronto
 *     ("2/5 prontos"). Tocar abre a tela de montar, que é onde as cinco linhas
 *     têm botão para resolver cada falta.
 *   **com peça** — o widget é a situação: a barra dos cinco estados da esteira,
 *     o nome do atual, e o que ainda fura a peça. Tocar abre o editor.
 *
 * Ele NÃO monta a minuta. Montar é decisão de gasto (um minuto de assistente) e
 * de resultado (peça com "[A CONFERIR]" no meio), e as duas se tomam na tela que
 * as explica — quando o portão de `minutarSePuder` abre, a peça já chega montada
 * e este widget mostra "montada sozinha".
 */
const ESTADOS = ["a_elaborar", "em_elaboracao", "revisao", "a_protocolar", "protocolada"];
const ROTULO = {
  a_elaborar: "A elaborar",
  em_elaboracao: "Em elaboração",
  revisao: "Em revisão",
  a_protocolar: "A protocolar",
  protocolada: "Protocolada",
};

export default function WidgetDaPeticao() {
  const { caso, provas, campos: esquema } = useEstado();
  const { ajustar } = useNavegacao();
  const pode = usePode();
  if (!caso || !pode("esteira:ver")) return null;

  const peca = (caso.pecas ?? [])[0];
  const abrir = () => ajustar({ peticao: true });

  /* SEM PEÇA: o convite, com a conta do que já está pronto. */
  if (!peca) {
    const insumos = insumosDaPeca({ caso, provas: provas ?? [], ficha: caso.pauta?.ficha, esquema });
    const prontos = insumos.filter((i) => i.estado === "ok").length;
    return (
      <Widget
        icone={ICONES.peca}
        titulo="Petição inicial"
        valor={`${prontos}/${insumos.length}`}
        tom={prontos === insumos.length ? "verde" : "papel"}
        aoTocar={abrir}
        dica="Montar a inicial a partir do acervo da casa"
        largo
      >
        <span className="text-apoio leading-[1.4] text-tinta-suave">
          {prontos === insumos.length
            ? "Tudo pronto para montar do acervo."
            : `${insumos.length - prontos === 1 ? "Falta 1 insumo" : `Faltam ${insumos.length - prontos} insumos`} — toque para ver o que resolve cada um.`}
        </span>
      </Widget>
    );
  }

  /* COM PEÇA: a situação. */
  const i = ESTADOS.indexOf(peca.estado);
  const furos = caso.pauta?.ficha?.faltam?.length ?? 0;
  const automatica = String(peca.autor_da_versao ?? "").includes("automático");
  const protocolada = peca.estado === "protocolada";

  return (
    <Widget
      icone={ICONES.peca}
      titulo="Petição inicial"
      /*
       * SEM número aqui, e é para não dizer duas coisas com a mesma forma.
       *
       * O estado da peça em "2/5" tem exatamente o desenho do "2/5" do widget
       * SEM peça — que conta insumos prontos. Duas contas diferentes com a mesma
       * cara, no mesmo lugar da coluna, e a pessoa lê a errada. Aqui o glance é a
       * BARRA (a mesma do trilho do processo) com o nome do estado embaixo; o
       * número não acrescenta nada que ela já não diga.
       */
      valor={null}
      /* Os furos pintam o widget e não se escrevem aqui: a conta com os campos
         marcados é da ficha, e a mesma frase em três lugares da coluna era
         ruído. */
      tom={protocolada ? "verde" : furos ? "ambar" : "papel"}
      aoTocar={abrir}
      dica="Abrir a petição — editor, conversa sobre a peça e o que falta"
      largo
    >
      {/* A barra dos cinco estados: o mesmo idioma do trilho do processo, e por
          isso não precisa de legenda. Só o atual leva nome. */}
      <span className="flex items-center gap-[3px]" aria-hidden="true">
        {ESTADOS.map((e, n) => (
          <span
            key={e}
            className={`h-[6px] flex-1 rounded-full ${
              n < i ? "bg-verde" : n === i ? (protocolada ? "bg-verde" : "bg-terra") : "bg-foreground/12"
            }`}
          />
        ))}
      </span>
      <span className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5">
        <span className="text-corpo leading-[1.3] font-semibold">{ROTULO[peca.estado] ?? peca.estado}</span>
        {automatica ? (
          <Etiqueta
            ponto
            cores={["color-mix(in oklab, var(--color-verde-fundo) 14%, transparent)", "var(--color-verde-fundo)"]}
          >
            montada sozinha
          </Etiqueta>
        ) : null}
      </span>
    </Widget>
  );
}
